import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

interface PageHeaderContextValue {
  title: string
  action: ReactNode
  titleIcon: ReactNode
  setTitle: (title: string) => void
  setAction: (action: ReactNode) => void
  setTitleIcon: (icon: ReactNode) => void
}

const PageHeaderContext = createContext<PageHeaderContextValue>({
  title: '',
  action: null,
  titleIcon: null,
  setTitle: () => {},
  setAction: () => {},
  setTitleIcon: () => {},
})

export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const [title, setTitle] = useState('')
  const [action, setAction] = useState<ReactNode>(null)
  const [titleIcon, setTitleIcon] = useState<ReactNode>(null)

  return (
    <PageHeaderContext.Provider value={{ title, action, titleIcon, setTitle, setAction, setTitleIcon }}>
      {children}
    </PageHeaderContext.Provider>
  )
}

/** Called by a page to register its header title. */
export function useSetPageTitle(title: string) {
  const { setTitle } = useContext(PageHeaderContext)
  useEffect(() => {
    setTitle(title)
    return () => setTitle('')
  }, [title, setTitle])
}

/** Called by a page to register an action button inline with the title. */
export function useSetPageAction(action: ReactNode) {
  const { setAction } = useContext(PageHeaderContext)
  useEffect(() => {
    setAction(action)
    return () => setAction(null)
  }, [action, setAction])
}

/** Called by a page to register an icon rendered inline before the header title. */
export function useSetPageTitleIcon(icon: ReactNode) {
  const { setTitleIcon } = useContext(PageHeaderContext)
  useEffect(() => {
    setTitleIcon(icon)
    return () => setTitleIcon(null)
  }, [icon, setTitleIcon])
}

/** Read the current page header state — used by Layout to render the header band. */
export function usePageHeader() {
  const { title, action, titleIcon, setAction } = useContext(PageHeaderContext)
  return { title, action, titleIcon, setAction }
}
